import { z } from "zod";

// Deliberately NOT providerIntegrationProviderSchema (asana|wrike): that enum
// is shared by providerBotIdentity/providerUserIdentity/providerIngress, which
// model Asana/Wrike-specific bot-identity and operational-ingress concepts
// that don't apply to email. Telemetry's provider/channel field is widened
// here instead, so the bot-identity surfaces stay untouched by the email
// channel addition.
export const providerIngressTelemetryChannelSchema = z.enum([
  "asana",
  "wrike",
  "email",
]);

export type ProviderIngressTelemetryChannel = z.infer<
  typeof providerIngressTelemetryChannelSchema
>;

export const providerIngressTelemetryStatusSchema = z.enum([
  "received",
  "accepted",
  "skipped",
  "dropped",
  "claimed",
  "routing_workflow_started",
  "routed",
  "reply_posted",
  "reply_failed",
  "failed_retryable",
  "failed_terminal",
  "echo_suppressed",
  // Email-only terminal success marker. Wrike/Asana treat
  // routing_workflow_started as terminal because christo's involvement ends
  // there (samba's routing workflow takes over asynchronously). For email,
  // christo itself owns the whole round trip synchronously in one Lambda
  // invocation, so it can — and should — stamp a definitive final state once
  // that round trip finishes, mirroring AskPunttEmailIngress.status="completed".
  "completed",
]);

export type ProviderIngressTelemetryStatus = z.infer<
  typeof providerIngressTelemetryStatusSchema
>;

export const providerIngressTelemetryTriggerTypeSchema = z.enum([
  "provider_comment",
  "webhook_signature_failure",
  // Postmark inbound webhook hit for the ask@puntt.ai direct address.
  "email_inbound",
  // Recovery path: postmark_inbound_sweeper polling Postmark's message API
  // for deliveries the webhook never durably recorded (oversized payloads,
  // stuck "scheduled"/"failed" Postmark states).
  "postmark_inbound_sweeper",
]);

export type ProviderIngressTelemetryTriggerType = z.infer<
  typeof providerIngressTelemetryTriggerTypeSchema
>;

// Mirrors samba's VALID_ROUTES exactly (ask_puntt_routing_workflow.py) — stored
// verbatim as passed through from Samba, never re-derived by Christo, for the
// Wrike/Asana provider-comment path. The email channel has no samba routing
// workflow (christo decides directly), but reuses the same two values that
// already fit its behavior: "existing_chat" (thread matched an existing
// AskPunttChat) and "new_project" (no match — a new project+shared chat is
// created), rather than introducing a parallel enum for the same concepts.
export const routeDecisionSchema = z.enum([
  "existing_chat",
  "existing_project",
  "new_project",
  "chat",
  "discard",
]);

export type RouteDecision = z.infer<typeof routeDecisionSchema>;

// The authoritative post-execution result, computed by Christo from its own
// mutation outcome — not a static lookup off routeDecision. See
// routeOutcome field comment in providerIngressTelemetrySchema for why.
export const routeOutcomeSchema = z.enum([
  "chat_created",
  "chat_reused",
  "project_created",
  "project_reused",
  "discarded",
  "not_applicable",
]);

export type RouteOutcome = z.infer<typeof routeOutcomeSchema>;

// Shared, provider-agnostic drop-reason vocabulary. Provider-specific detail
// (which anchor attribute failed to match, which contact lookup was
// ambiguous, etc.) belongs in providerMetadata, not a new reason string here.
// Mirrored in Python for parity — see the vocabulary parity test.
export const providerIngressTelemetryDropReasonSchema = z.enum([
  "missing_comment_id",
  "missing_bot_identity",
  "ambiguous_bot_identity",
  "authored_by_provider_bot",
  "missing_structured_mention",
  "empty_query",
  "comment_not_found",
  "ambiguous_comment_selection",
  "missing_comment_selection_fields",
  "missing_actor_identifier",
  "actor_user_not_resolved",
  "integration_disabled",
  "workflow_start_failed",
  // Email-channel-specific reasons with no existing provider-comment
  // equivalent. Everything else about the email drop vocabulary (missing
  // sender identifier, sender not resolved to a Puntt user, empty content)
  // reuses the values above verbatim — see the drop-reason mapper in
  // services/christo/app/handlers/catalyst/askPunttEmailIngress.ts.
  "no_eligible_enterprise",
  "demo_target_unavailable",
  "missing_linked_enterprise",
  "actor_not_onboarded",
  "thread_access_denied",
]);

export type ProviderIngressTelemetryDropReason = z.infer<
  typeof providerIngressTelemetryDropReasonSchema
>;

export const deliveryAttemptSchema = z.object({
  receivedAt: z.date(),
  sourceEventType: z.string().optional(),
  outcome: z.string(),
});

export type DeliveryAttempt = z.infer<typeof deliveryAttemptSchema>;

export const providerIngressTelemetryErrorDetailSchema = z.object({
  message: z.string(),
  code: z.string().optional(),
  timestamp: z.date(),
});

export const providerIngressTelemetrySchema = z.object({
  // Optional: the ask@puntt.ai inbound webhook is a single global endpoint —
  // the enterprise is only discovered later, once the sender/thread resolves
  // (see resolveAskPunttDirectEnterpriseId / existing-thread lookup in
  // askPunttEmailIngress.ts). Wrike/Asana always know enterpriseId from the
  // callback URL, so they continue to set it on the very first write.
  // Backfilled via markClaimed's context once resolved for email.
  enterpriseId: z.string().optional(),
  provider: providerIngressTelemetryChannelSchema,
  // Optional: absent for Asana signature-failure docs, which are verified
  // before the body is ever parsed — no task id is knowable at that point.
  providerTaskId: z.string().optional(),
  // Omitted entirely when unknown — never set to null. A partial unique
  // index on this field matches an explicit null too, so a null value would
  // still collide across documents. See the service layer's write helpers.
  providerCommentId: z.string().optional(),
  providerEventId: z.string().optional(),
  providerAttachmentId: z.string().optional(),
  providerIngressKey: z.string().optional(),
  // For the email channel this holds AskPunttEmailIngressModel._id (there is
  // no per-provider operational-ingress table for email) — stamped at the
  // same markClaimed call Wrike/Asana use for their ProviderIngress._id.
  providerIngressId: z.string().optional(),
  externalThreadMappingId: z.string().optional(),
  // Email-only join keys. Never the raw sender/recipient address, subject,
  // or body — see the "no raw content" rule below.
  emailIngressKey: z.string().optional(),
  postmarkMessageId: z.string().optional(),
  askPunttMessageId: z.string().optional(),
  // Temporal workflow id for the AskPuntt routing workflow this ingress
  // started — the exact value passed to Client.workflow.start, so an
  // operator can jump from this document straight to the workflow in the
  // Temporal UI. Stamped at the same call as status="routing_workflow_started".
  routingWorkflowId: z.string().optional(),
  // "<provider>-<enterpriseId>-<providerTaskId>", matching
  // IntegrationEvent.composite_task_id exactly. Both-or-neither with
  // providerTaskId.
  compositeTaskId: z.string().optional(),
  triggerType: providerIngressTelemetryTriggerTypeSchema,
  sourceEventType: z.string().optional(),
  actorProviderUserId: z.string().optional(),
  resolvedPunttUserId: z.string().optional(),
  botProviderUserId: z.string().optional(),
  linkedChatId: z.string().optional(),
  linkedProjectId: z.string().optional(),
  routeDecision: routeDecisionSchema.optional(),
  // Required with a default so dashboard aggregation never has to
  // special-case a missing value. Set to "not_applicable" at document
  // creation; only Christo's route-endpoint stamp ever moves it off that
  // default, in the same atomic update as routeDecision/status="routed".
  routeOutcome: routeOutcomeSchema,
  // For the email channel this holds the outbound Postmark MessageID from the
  // one reply email christo itself sends (the oversize-recovery notice in
  // askPunttOversizeRecovery.ts) — there is no separate "outbound email id"
  // field because it is the same concept as an outbound provider comment id.
  outboundCommentId: z.string().optional(),
  status: providerIngressTelemetryStatusSchema,
  dropReason: providerIngressTelemetryDropReasonSchema.optional(),
  errorDetail: providerIngressTelemetryErrorDetailSchema.optional(),
  deliveryAttempts: z.array(deliveryAttemptSchema),
  receivedAt: z.date().optional(),
  claimedAt: z.date().optional(),
  routedAt: z.date().optional(),
  completedAt: z.date().optional(),
  repliedAt: z.date().optional(),
  failedAt: z.date().optional(),
  providerMetadata: z.record(z.unknown()).optional(),
  env: z.enum(["prod", "dev", "staging", "local"]),
  eventContractVersion: z.number(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export type ProviderIngressTelemetry = z.infer<
  typeof providerIngressTelemetrySchema
>;
